import { authApi } from '../api/auth';
import { ApiError } from '../api/client';
import { clearStudentLocalState } from './logout-hygiene';

export class StudentLogoutRetryableError extends Error {
  constructor() {
    super('student_logout_retryable');
    this.name = 'StudentLogoutRetryableError';
  }
}

export interface StudentLogoutDependencies {
  requestLogout: () => Promise<void>;
  clearLocalState: () => void;
  hardReplaceLogin: () => void;
  reportTerminalError?: (error: unknown) => void;
}

export interface StudentLogoutOrchestrator {
  logout: () => Promise<void>;
}

export function createStudentLogoutOrchestrator(
  dependencies: StudentLogoutDependencies,
): StudentLogoutOrchestrator {
  let latchedPromise: Promise<void> | null = null;

  const reportTerminalError = (error: unknown) => {
    try {
      dependencies.reportTerminalError?.(error);
    } catch {
      // Reporting must never turn a terminal server outcome into a retry.
    }
  };

  return {
    logout: () => {
      if (latchedPromise) return latchedPromise;

      let request: Promise<void>;
      try {
        request = dependencies.requestLogout();
      } catch (error) {
        request = Promise.reject(error);
      }

      const operation = request
        .catch((error: unknown) => {
          if (error instanceof ApiError && error.status === 401) return;
          throw new StudentLogoutRetryableError();
        })
        .then(() => {
          try {
            dependencies.clearLocalState();
          } catch (error) {
            reportTerminalError(error);
          }
          try {
            dependencies.hardReplaceLogin();
          } catch (error) {
            reportTerminalError(error);
          }
        });

      latchedPromise = operation;
      void operation.catch(() => {
        if (latchedPromise === operation) latchedPromise = null;
      });
      return operation;
    },
  };
}

export function hardReplaceStudentLogin(): void {
  window.location.replace('/login');
}

const productionStudentLogout = createStudentLogoutOrchestrator({
  requestLogout: () => authApi.logout(),
  clearLocalState: clearStudentLocalState,
  hardReplaceLogin: hardReplaceStudentLogin,
  reportTerminalError: () => {
    console.error('[student-logout] terminal local effect failed');
  },
});

export const studentLogout = productionStudentLogout;
